import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, ScrollText } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupAdminLog, type AdminGroupLogEntry } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.22 — Admin harakatlar jurnali. GET /admin/groups/:id/admin-log (backend
 * `e61ca2c`). Foydalanuvchiga ko'rinadigan 48h oynasidan farqli — to'liq tarix.
 */
export function GroupAdminLog() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminGroupLogEntry>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
      { key: 'action', header: t.col.action, type: 'text', width: 160 },
      {
        key: 'actor_id',
        header: t.groups.actor,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.actor_id}`} className="text-accent hover:underline">
            {r.actor_username ? `@${r.actor_username}` : `#${r.actor_id}`}
          </Link>
        ),
      },
      {
        key: 'target_id',
        header: t.col.target,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) =>
          r.target_id ? (
            <Link to={`/users/${r.target_id}`} className="text-accent hover:underline">
              {r.target_username ? `@${r.target_username}` : `#${r.target_id}`}
            </Link>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        key: 'meta',
        header: t.col.detail,
        type: 'longtext',
        minWidth: 200,
        sortable: false,
        render: (r) =>
          r.meta && Object.keys(r.meta).length ? (
            <span className="font-mono text-xs text-secondary">{JSON.stringify(r.meta)}</span>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupAdminLog(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.adminLog}
        screenId="A4.22"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminGroupLogEntry>
        screenId="A4.22"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => `${r.created_at}-${r.actor_id}-${r.action}`}
        exportable
        emptyState={{ icon: <ScrollText size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
